import { agaveService } from 'app/services/agave.service';
import {
  HighnoteTransactionStatus,
  ERPPostingStatus,
  HighnoteTransactionEvent,
  AgaveAccount,
  CreateAgaveExpenseInput,
  CreateAgaveExpenseLineItemInput,
  CreateAgaveExpensePaymentMethodInput,
  AgaveAPInvoicesCreateExpenseInput,
  AgaveAPInvoicesLineItemInput,
} from 'API';
import type { ErpPostingStatus } from 'CDK_API';
import { PostingContext } from './types';
import { createPostingSubscription } from './sharedPostingUtils';
import { formatAmountForAgavePost, stripPaidolPrefix } from 'app/services/agave/erpUtils';
import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';

// Constants
const SPEEDCHAIN_MERCHANT_NAME = 'SPEEDCHAIN';

/**
 * Builds common expense fields for QBO posting.
 * Shared by both expense and AP invoice payload builders.
 */
function buildQBOExpenseFields(
  txn: HighnoteTransactionEvent,
  agaveAccount: AgaveAccount | undefined,
  overrides = {}
): {
  amountStr: string;
  amountValue: number;
  description: string;
  erp: string;
  payment_date: string;
  payment_number: string;
  cost_code_sid?: string;
  customer_id?: string;
  ledger_account_id?: string;
  vendor_id?: string;
} {
  const { amount, amountValue } = formatAmountForAgavePost(txn.approvedAmount?.value ?? 0, txn.status);
  return {
    amountStr: amount,
    amountValue,
    description: (txn.merchantName || '') + (txn.note ? ' - ' + txn.note : ''),
    erp: agaveAccount?.source_system?.slug || AgaveSourceSystem.QUICKBOOKS_ONLINE,
    payment_date: new Date(txn.createdAt).toISOString().split('T')[0],
    payment_number: txn.id.substring(0, 10),
    ...(txn.agaveCostCode?.source_id && {
      cost_code_sid: `source_id:${txn.agaveCostCode.source_id}`,
    }),
    ...(txn.agaveProject?.source_id && { customer_id: `source_id:${txn.agaveProject.source_id}` }),
    ...(txn.agaveGlCodeId && { ledger_account_id: stripPaidolPrefix(txn.agaveGlCodeId) }),
    ...(txn.agaveVendorId && { vendor_id: stripPaidolPrefix(txn.agaveVendorId) }),
    ...overrides,
  };
}

/**
 * Builds QBO Expense payload for queue-based posting.
 * Used when bills are disabled in QBO settings.
 */
export function buildQBOExpensePayload(
  transaction: HighnoteTransactionEvent,
  agaveAccount: AgaveAccount | undefined,
  agaveAccountToken: string
): CreateAgaveExpenseInput {
  const {
    amountStr,
    cost_code_sid,
    customer_id,
    description,
    erp,
    ledger_account_id,
    payment_date,
    payment_number,
    vendor_id,
  } = buildQBOExpenseFields(transaction, agaveAccount);

  const line_items: CreateAgaveExpenseLineItemInput[] = [
    {
      amount: amountStr,
      description,
      quantity: '1.0',
      status: 'NotBillable',
      unit_cost: amountStr,
      ...(ledger_account_id && { ledger_account_id }),
      ...(customer_id && { customer_id }),
      // Use type field for cost type (maps to CLASS column)
      ...(transaction.agaveCostType?.name && { type: transaction.agaveCostType?.name }),
      // handle case with cost code but no gl code
      ...(cost_code_sid && !ledger_account_id && { item_id: cost_code_sid }),
    },
  ];

  // If we have both a cost code and a gl code, add a second line item
  if (transaction.agaveCostCodeId && transaction.agaveGlCodeId) {
    line_items.push({
      amount: '0.00',
      description: '',
      item_id: cost_code_sid,
      quantity: '1.0',
      status: 'NotBillable',
      unit_cost: '0.00',
    });
  }

  const expense: CreateAgaveExpenseInput = {
    account_token: agaveAccountToken,
    description,
    erp,
    line_items,
    merchantName: SPEEDCHAIN_MERCHANT_NAME,
    number: payment_number,
    payment_date,
    payment_method: CreateAgaveExpensePaymentMethodInput.CreditCard,
    ...(vendor_id && { vendor_id }),
  };
  return expense;
}

/**
 * Builds QBO AP Invoice/Bill payload for queue-based posting.
 * Used when bills are enabled in QBO settings.
 */
export function buildQBOAPInvoicePayload(
  transaction: HighnoteTransactionEvent,
  agaveAccount: AgaveAccount | undefined,
  agaveAccountToken: string
): AgaveAPInvoicesCreateExpenseInput {
  const {
    amountStr,
    cost_code_sid,
    customer_id,
    description,
    erp,
    ledger_account_id,
    payment_date,
    payment_number,
    vendor_id,
  } = buildQBOExpenseFields(transaction, agaveAccount);

  const line_items: AgaveAPInvoicesLineItemInput[] = [
    {
      amount: amountStr,
      description,
      quantity: '1.0',
      status: 'NotBillable',
      unit_cost: amountStr,
      ...(ledger_account_id && { ledger_account_id }),
      ...(customer_id && { customer_id }),
      ...(transaction.agaveCostTypeId && { cost_type_id: stripPaidolPrefix(transaction.agaveCostTypeId) }),
      // handle case with cost code but no gl code
      ...(cost_code_sid && !ledger_account_id && { item_id: cost_code_sid }),
    },
  ];

  // If we have both a cost code and a gl code, add a second line item
  if (transaction.agaveCostCodeId && transaction.agaveGlCodeId) {
    line_items.push({
      amount: '0.00',
      description: '',
      item_id: cost_code_sid,
      quantity: '1.0',
      status: 'NotBillable',
      unit_cost: '0.00',
    });
  }

  const expense: AgaveAPInvoicesCreateExpenseInput = {
    account_token: agaveAccountToken,
    description,
    erp,
    line_items,
    issue_date: payment_date,
    merchantName: SPEEDCHAIN_MERCHANT_NAME,
    number: payment_number,
    ...(vendor_id && { vendor_id }),
  };
  return expense;
}

/**
 * Recursively removes undefined values from an object.
 * GraphQL's AWSJSON type doesn't accept undefined values.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function removeUndefinedValues(obj: any): any {
  if (obj === null || obj === undefined) {
    return null;
  }
  if (Array.isArray(obj)) {
    return obj.map(removeUndefinedValues);
  }
  if (typeof obj === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cleaned: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined) {
        cleaned[key] = removeUndefinedValues(obj[key]);
      }
    }
    return cleaned;
  }
  return obj;
}

/**
 * Validates QBO posting data structure before sending to GraphQL.
 * Ensures required fields are present and data types are correct.
 */
function validateQBOPostingData(postingData: any, postingType: string): void {
  if (!postingData || typeof postingData !== 'object') {
    throw new Error('Invalid posting data: posting data must be an object');
  }

  // Common required fields for both AP_INVOICE and EXPENSE
  if (!postingData.account_token || typeof postingData.account_token !== 'string') {
    throw new Error('Invalid posting data: account_token is required and must be a string');
  }

  if (!postingData.number || typeof postingData.number !== 'string') {
    throw new Error('Invalid posting data: number is required and must be a string');
  }

  if (
    !postingData.line_items ||
    !Array.isArray(postingData.line_items) ||
    postingData.line_items.length === 0
  ) {
    throw new Error('Invalid posting data: line_items is required and must be a non-empty array');
  }

  // Validate line items
  postingData.line_items.forEach((item: any, index: number) => {
    if (!item.amount || typeof item.amount !== 'string') {
      throw new Error(`Invalid posting data: line_items[${index}].amount is required and must be a string`);
    }
    if (item.quantity && typeof item.quantity !== 'string') {
      throw new Error(`Invalid posting data: line_items[${index}].quantity must be a string if provided`);
    }
  });

  // Type-specific validation
  if (postingType === 'AP_INVOICE') {
    // AgaveAPInvoicesCreateExpenseInput validation
    if (!postingData.issue_date || typeof postingData.issue_date !== 'string') {
      throw new Error('Invalid posting data: issue_date is required for AP_INVOICE and must be a string');
    }
    // issue_date should be used, not payment_date
    if (postingData.payment_date) {
      // eslint-disable-next-line no-console
      console.warn('AP_INVOICE posting data contains payment_date, but should use issue_date instead');
    }
  } else if (postingType === 'EXPENSE') {
    // CreateAgaveExpenseInput validation
    if (!postingData.payment_date || typeof postingData.payment_date !== 'string') {
      throw new Error('Invalid posting data: payment_date is required for EXPENSE and must be a string');
    }
    if (!postingData.payment_method) {
      throw new Error('Invalid posting data: payment_method is required for EXPENSE');
    }
  }
}

export interface QBOPostingParams {
  transactionId: string;
  paidolId: string;
  transaction: HighnoteTransactionEvent;
  agaveAccount?: AgaveAccount;
  agaveAccountToken: string;
  useBills: boolean;
  qboUtil: any; // qboUtil for refund logic only (buildRefundForAgaveResolver, buildCreateAgaveExpenseFromCreditCardCredit)
  createCreditCardCreditQBO?: any; // Redux action for refunds
  updateTransaction?: any; // Redux action
  removeTypename?: (obj: any) => any;
}

/**
 * Handles QBO posting via queue (both AP Invoice and Expense)
 * Also handles refunds via direct passthrough (not queue-based)
 */
export async function postQBOTransaction(params: QBOPostingParams, context: PostingContext): Promise<void> {
  const {
    transactionId,
    paidolId,
    transaction: txn,
    agaveAccount,
    agaveAccountToken,
    useBills,
    qboUtil,
    createCreditCardCreditQBO,
    updateTransaction,
    removeTypename,
  } = params;

  const { dispatch, enqueueSnackbar, t, setIsAgaveLoading, setTransactionPosting, captureException } =
    context;

  // Handle refunds separately - they use direct passthrough, not queue
  if (txn.status === HighnoteTransactionStatus.REFUNDED) {
    setIsAgaveLoading(true);

    return dispatch(
      createCreditCardCreditQBO({
        input: qboUtil.buildRefundForAgaveResolver(txn, agaveAccount, agaveAccountToken),
      })
    )
      .unwrap()
      .then((creditCardCredit: any) => {
        if (creditCardCredit && 'id' in creditCardCredit) {
          const expense = qboUtil.buildCreateAgaveExpenseFromCreditCardCredit(creditCardCredit);

          // Update transaction status immediately in frontend
          dispatch(
            updateTransaction({
              id: transactionId,
              erpApprovalStatus: true,
              agaveExpense: removeTypename ? removeTypename(expense) : expense,
            })
          );
          enqueueSnackbar(t('submitExpense.success'), { variant: 'success' });
        } else {
          enqueueSnackbar((creditCardCredit as any)?.message || 'Agave Error', {
            variant: 'error',
          });
        }
      })
      .catch((error: Error) => {
        enqueueSnackbar(error.message || t('submitExpense.error'), { variant: 'error' });
      })
      .finally(() => {
        setIsAgaveLoading(false);
        setTransactionPosting({ id: transactionId, isPosting: false });
      });
  }

  // Use queue-based posting for regular transactions (both AP Invoice and Expense)
  // IMPORTANT: Do NOT set posting state until we know the mutation succeeded
  // This prevents the transaction from getting stuck in "posting" if there's a GraphQL validation error
  setIsAgaveLoading(true);

  let responseStatus: ErpPostingStatus | undefined;

  try {
    // Build posting data based on QBO settings
    let postingData: CreateAgaveExpenseInput | AgaveAPInvoicesCreateExpenseInput;
    let postingType: string;

    if (useBills) {
      // Use AP invoice posting for regular transactions when bills are enabled
      postingData = buildQBOAPInvoicePayload(txn, agaveAccount, agaveAccountToken);
      postingType = 'AP_INVOICE';
    } else {
      // Use expense posting for regular transactions when bills are disabled
      postingData = buildQBOExpensePayload(txn, agaveAccount, agaveAccountToken);
      postingType = 'EXPENSE';
    }

    // Validate postingData structure before sending
    validateQBOPostingData(postingData, postingType);

    // Clean postingData to remove undefined values (GraphQL AWSJSON doesn't accept undefined)
    const cleanedPostingData = removeUndefinedValues(postingData);

    // Double-check required fields after cleaning
    if (!cleanedPostingData.account_token || !cleanedPostingData.number || !cleanedPostingData.line_items) {
      throw new Error('Invalid posting data: required fields missing after cleaning');
    }

    // IMPORTANT: GraphQL's AWSJSON scalar type expects a JSON STRING, not an object
    // Stringify the cleaned posting data before sending
    const postingDataString = JSON.stringify(cleanedPostingData);

    // Set up subscription BEFORE starting the async process
    // This ensures we catch status updates even if posting completes very quickly
    const subscription = createPostingSubscription(transactionId, context, {
      onCompleted: () => {
        enqueueSnackbar(useBills ? t('submitExpense.successBill') : t('submitExpense.success'), {
          variant: 'success',
        });
      },
    });

    // Start the async QBO posting via the CDK v2-agave Lambda (SEE-308).
    // agaveService throws on transport / GraphQL errors, so we only need to
    // branch on the returned status.
    const result = await agaveService.startQBOPosting({
      input: {
        transactionId,
        paidolId,
        erp: AgaveSourceSystem.QUICKBOOKS_ONLINE,
        postingType: postingType,
        postingData: postingDataString, // AWSJSON expects a JSON string, not an object
      },
    });

    responseStatus = result?.status;

    if (responseStatus === ERPPostingStatus.PENDING) {
      enqueueSnackbar(useBills ? t('submitExpense.postingBill') : t('submitExpense.postingExpense'), {
        variant: 'info',
      });
    } else if (responseStatus === ERPPostingStatus.COMPLETED) {
      // Transaction was already posted — dispatcher rejected the re-post (SEE-726)
      subscription.unsubscribe();
      setTransactionPosting({ id: transactionId, isPosting: false });
      setIsAgaveLoading(false);
      enqueueSnackbar(t('submitExpense.alreadyPosted'), { variant: 'info' });
      return;
    } else if (responseStatus === ERPPostingStatus.PROCESSING) {
      // Posting already in progress — dispatcher rejected the duplicate request (SEE-726)
      subscription.unsubscribe();
      setTransactionPosting({ id: transactionId, isPosting: false });
      setIsAgaveLoading(false);
      enqueueSnackbar(t('submitExpense.postingInProgress'), { variant: 'info' });
      return;
    } else {
      subscription.unsubscribe();
      // If status is FAILED or unknown - check the response
      throw new Error(result?.error || 'Failed to start QBO posting');
    }
  } catch (error) {
    // IMPORTANT: Clear posting state FIRST to prevent UI from showing "posting" status
    // This handles GraphQL validation errors that occur before the mutation reaches the backend
    setTransactionPosting({ id: transactionId, isPosting: false });
    setIsAgaveLoading(false);

    captureException(error);
    const errorMessage = (error as Error).message || t('submitExpense.error');
    enqueueSnackbar(errorMessage, { variant: 'error' });

    // Ensure transaction status is updated in Redux. Carry the responseStatus we
    // observed before throwing (e.g. FAILED) rather than always clearing to null,
    // so the sidesheet banner (gated on erpPostingStatus === 'FAILED') still renders.
    const { applyLocalTransactionUpdates } = context;
    dispatch(
      applyLocalTransactionUpdates([
        {
          id: transactionId,
          changes: {
            erpPostingStatus: responseStatus ?? null,
            erpPostingError: errorMessage,
          },
        },
      ]) as any
    );
  }
}
